/**
 * 请求日志的「用量看板」视图（Alice 的 log-dash-only 区域）：KPI、模型趋势、占比、成败、模型明细、来源与账号排行。
 *
 * 背景：Alice 把看板和调用明细放在同一个页面用开关切换，筛选条件共用。本组件只负责渲染看板数据，
 *   取数、筛选、视图切换都在 Logs.tsx，这里通过回调把「看明细」和排行点击交回去。
 * 设计意图：数据按后端聚合好的序列画图，前端不再汇总；趋势、占比用同一份 model_trend，保证两张图口径一致；
 *   金额统一从整数 unit 换成花瓣，成本未知的请求不进毛利率分母（后端给了 known_cost_* 字段）。
 * 关键约束：图表配置用 useMemo 缓存，数据没变不重建图；颜色是图表系列色（Alice 同样写死），不进皮肤变量。
 */
import { useMemo, useState } from 'react'
import type { ChartConfiguration } from 'chart.js'
import type { DashDimension, DashMetrics, LogDashboard } from '@ap/api-client/admin-runtime'
import { petals as toPetals } from '@ap/api-client/money'
import { AG_runtime as G } from '@ap/i18n/admin-runtime'
import { useI18n } from '@ap/i18n'
import { Stat, Stats } from '../../../alice/kit.tsx'
import { ChartCanvas, baseChartOptions, fmtInt, fmtLatency, fmtPct, fmtTokens, localOffsetMinutes, useMoney } from '../../../alice/kit-runtime.tsx'

export type DashMetric = 'requests' | 'tokens' | 'user_cost'

const PALETTE = ['#199cff', '#7c3aed', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16', '#6366f1', '#14b8a6', '#f97316']
const OTHER_COLOR = '#cbd5e1'
const colorOf = (i: number, key: string) => (key === '__other__' ? OTHER_COLOR : PALETTE[i % PALETTE.length])

type SortKey = 'requests' | 'success' | 'users' | 'input' | 'output' | 'cache' | 'charge' | 'upstream' | 'margin' | 'avg' | 'p95'

const num = (v: number | null | undefined) => (v === null || v === undefined ? -1 : v)
const rate = (a: number, b: number) => (b > 0 ? a / b : null)
const margin = (m: DashMetrics) => (m.known_cost_user_cost > 0 ? (m.known_cost_user_cost - m.known_upstream_cost) / m.known_cost_user_cost : null)

const SORTERS: Record<SortKey, (m: DashMetrics) => number> = {
  requests: (m) => m.requests,
  success: (m) => num(rate(m.success_requests, m.requests)),
  users: (m) => m.users,
  input: (m) => m.input_tokens,
  output: (m) => m.output_tokens,
  cache: (m) => num(rate(m.cached_tokens, m.input_tokens)),
  charge: (m) => m.user_cost,
  upstream: (m) => m.known_upstream_cost,
  margin: (m) => num(margin(m)),
  avg: (m) => num(m.avg_duration_ms),
  p95: (m) => num(m.p95_duration_ms),
}

/** 看板窗口的时间在后端以 UTC 返回，这里转成浏览器本地显示。 */
function localStamp(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

function tzLabel(): string {
  const m = localOffsetMinutes()
  const sign = m >= 0 ? '+' : '-'
  const a = Math.abs(m)
  return `UTC${sign}${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`
}

/** bucket 是已经换算成浏览器时区的本地时间串，按小时显示「月-日 时:00」，按天显示「月-日」。 */
function bucketLabel(bucket: string, hourly: boolean): string {
  const date = bucket.slice(5, 10)
  return hourly ? `${date} ${bucket.slice(11, 13)}:00` : date
}

export function LogDashboard({
  data,
  loading,
  error,
  dim,
  metric,
  userFilter,
  onDim,
  onMetric,
  onViewModel,
  onPickSource,
  onPickUser,
}: {
  data: LogDashboard | null
  loading: boolean
  error: string
  dim: DashDimension
  metric: DashMetric
  userFilter: boolean
  onDim: (d: DashDimension) => void
  onMetric: (m: DashMetric) => void
  onViewModel: (key: string) => void
  onPickSource: (s: string) => void
  onPickUser: (u: string) => void
}) {
  const { t } = useI18n()
  const money = useMoney()
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'requests', desc: true })

  const metricName = metric === 'requests' ? t(G.dash.metricRequests) : metric === 'tokens' ? t(G.dash.metricTokens) : t(G.dash.metricCost)
  const unit = metric === 'requests' ? t(G.dash.unitRequests) : metric === 'tokens' ? t(G.dash.unitTokens) : t(G.dash.unitCost)

  const charts = useMemo(() => {
    if (!data) return null
    const hourly = data.window.bucket === 'hour'
    const labels = data.trend.map((r) => bucketLabel(r.bucket, hourly))
    const conv = (v: number) => (metric === 'user_cost' ? toPetals(v) : v)
    const series = data.model_trend.map((s, i) => ({
      key: s.key,
      label: s.label || t(G.dash.other),
      color: colorOf(i, s.key),
      values: s[metric].map(conv),
    }))
    const base = baseChartOptions()
    const trend: ChartConfiguration = {
      type: 'bar',
      data: { labels, datasets: series.map((s) => ({ label: s.label, data: s.values, backgroundColor: s.color, borderRadius: 3, stack: 's' })) },
      options: { ...base, scales: { x: { ...base.scales.x, stacked: true }, y: { ...base.scales.y, stacked: true } } },
    }
    const totals = series.map((s) => s.values.reduce((a, b) => a + b, 0))
    const sum = totals.reduce((a, b) => a + b, 0)
    const share: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: { labels: series.map((s) => s.label), datasets: [{ data: totals, backgroundColor: series.map((s) => s.color), borderWidth: 0 }] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: {
          legend: { position: 'right', labels: { font: { size: 10 }, usePointStyle: true, pointStyle: 'circle', boxWidth: 6 } },
          tooltip: { callbacks: { label: (c) => ` ${c.label}: ${Number(c.raw).toLocaleString()} (${sum ? ((Number(c.raw) / sum) * 100).toFixed(1) : '0.0'}%)` } },
        },
      },
    }
    const status: ChartConfiguration = {
      type: 'bar',
      data: {
        labels,
        datasets: [
          { label: t(G.dash.success), data: data.trend.map((r) => r.success_requests), backgroundColor: '#10b981', borderRadius: 3, stack: 's' },
          { label: t(G.dash.failed), data: data.trend.map((r) => r.failed_requests), backgroundColor: '#ef4444', borderRadius: 3, stack: 's' },
        ],
      },
      options: {
        ...base,
        scales: { x: { ...base.scales.x, stacked: true }, y: { ...base.scales.y, stacked: true } },
        plugins: {
          ...base.plugins,
          tooltip: {
            callbacks: {
              afterBody: (items) => {
                const r = data.trend[items[0]?.dataIndex ?? 0]
                return r ? [t(G.dash.failRate, { r: fmtPct(rate(r.failed_requests, r.requests)) }), t(G.dash.totalLabel, { v: fmtInt(r.requests) })] : []
              },
            },
          },
        },
      },
    }
    return { trend, share, status, colors: new Map(series.map((s) => [s.key, s.color])) }
  }, [data, metric, t])

  const models = useMemo(() => {
    if (!data) return []
    const f = SORTERS[sort.key]
    return [...data.models].sort((a, b) => (sort.desc ? f(b) - f(a) : f(a) - f(b)))
  }, [data, sort])

  if (error && !data) return <div className="empty">{t(G.common.loadFailed, { msg: error })}</div>
  if (!data || !charts) return <div className="empty">{loading ? t(G.dash.aggregating) : t(G.dash.waiting)}</div>

  const s = data.summary
  const hint = data.window.start_defaulted ? (userFilter ? t(G.dash.defaultMonth) : `${t(G.dash.defaultWeek)} · ${t(G.dash.noUserLimit)}`) : ''
  const th = (key: SortKey, label: string) => (
    <th className={sort.key === key ? 'active' : ''} onClick={() => setSort((p) => ({ key, desc: p.key === key ? !p.desc : true }))}>
      {label}
      {sort.key === key ? (sort.desc ? ' ↓' : ' ↑') : ''}
    </th>
  )
  const maxReq = Math.max(1, ...data.models.map((m) => m.requests))

  return (
    <>
      <div className="log-dash-bar">
        <div className="log-dash-scope">
          {t(G.dash.scope, {
            start: localStamp(data.window.start),
            end: localStamp(data.window.end),
            bucket: data.window.bucket === 'hour' ? t(G.dash.bucketHour) : t(G.dash.bucketDay),
            n: data.model_count,
            dim: dim === 'shell' ? t(G.dash.dimShell) : t(G.dash.dimUpstream),
          })}
          {hint && ` · ${hint}`}
        </div>
        <div className="log-dash-controls">
          <span className="log-dash-control-label">{t(G.dash.dimLabel)}</span>
          <div className="period-switch" role="tablist">
            <button type="button" className={dim === 'shell' ? 'active' : ''} title={t(G.dash.dimShellTip)} onClick={() => onDim('shell')}>
              {t(G.dash.dimShell)}
            </button>
            <button type="button" className={dim === 'upstream' ? 'active' : ''} title={t(G.dash.dimUpstreamTip)} onClick={() => onDim('upstream')}>
              {t(G.dash.dimUpstream)}
            </button>
          </div>
          <span className="log-dash-control-label">{t(G.dash.metricLabel)}</span>
          <div className="period-switch" role="tablist">
            {(['requests', 'tokens', 'user_cost'] as const).map((m) => (
              <button key={m} type="button" className={metric === m ? 'active' : ''} onClick={() => onMetric(m)}>
                {m === 'requests' ? t(G.dash.metricRequests) : m === 'tokens' ? t(G.dash.metricTokens) : t(G.dash.metricCost)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Stats>
        <Stat
          label={t(G.dash.kpiRequests)}
          value={fmtInt(s.requests)}
          sub={t(G.dash.kpiRequestsSub, { r: fmtPct(rate(s.success_requests, s.requests)), f: fmtInt(s.failed_requests), u: fmtInt(s.users) })}
        />
        <Stat
          label={t(G.dash.kpiTokens)}
          value={fmtTokens(s.input_tokens + s.output_tokens)}
          sub={t(G.dash.kpiTokensSub, { i: fmtTokens(s.input_tokens), o: fmtTokens(s.output_tokens), c: fmtPct(rate(s.cached_tokens, s.input_tokens)) })}
        />
        <Stat label={t(G.dash.kpiCharge)} value={money.petals(s.user_cost)} sub={t(G.dash.kpiChargeSub, { n: fmtInt(s.billed_requests) })} />
        <Stat
          label={t(G.dash.kpiUpstream)}
          value={s.known_cost_requests > 0 ? money.usd(s.known_upstream_cost, 4) : '—'}
          sub={t(G.dash.kpiUpstreamSub, { m: fmtPct(margin(s)), c: fmtPct(rate(s.known_cost_requests, s.known_cost_requests + s.unknown_cost_requests)) })}
        />
      </Stats>

      <div className="dashboard-chart-grid" style={{ marginTop: 12 }}>
        <div className="dashboard-chart-card chart-wide">
          <div className="chart-card-head">
            <span>{t(G.dash.trendTitle, { metric: metricName })}</span>
            <small>{t(G.dash.trendHint, { bucket: data.window.bucket === 'hour' ? t(G.dash.hourly) : t(G.dash.daily), unit, tz: tzLabel() })}</small>
          </div>
          <div className="chart-h-260">
            <ChartCanvas config={charts.trend} />
          </div>
        </div>
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>{t(G.dash.shareTitle)}</span>
            <small>{t(G.dash.shareHint, { metric: metricName })}</small>
          </div>
          <div className="chart-h-240">
            <ChartCanvas config={charts.share} />
          </div>
        </div>
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>{t(G.dash.statusTitle)}</span>
            <small>{t(G.dash.statusHint)}</small>
          </div>
          <div className="chart-h-240">
            <ChartCanvas config={charts.status} />
          </div>
        </div>
      </div>

      <div className="ops-section-card log-dash-models">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(G.dash.modelsTitle)}</div>
            <div className="ops-section-copy">{t(G.dash.modelsHint)}</div>
          </div>
        </div>
        <div className="table-inner">
          <table className="log-dash-table">
            <thead>
              <tr>
                <th style={{ cursor: 'default' }}>{t(G.dash.colModel)}</th>
                {th('requests', t(G.dash.colRequests))}
                {th('success', t(G.dash.colSuccessRate))}
                {th('users', t(G.dash.colUsers))}
                {th('input', t(G.dash.colInput))}
                {th('output', t(G.dash.colOutput))}
                {th('cache', t(G.dash.colCacheRate))}
                {th('charge', t(G.dash.colCharge))}
                {th('upstream', t(G.dash.colUpstream))}
                {th('margin', t(G.dash.colMargin))}
                {th('avg', t(G.dash.colAvg))}
                {th('p95', t(G.dash.colP95))}
                <th style={{ cursor: 'default' }} />
              </tr>
            </thead>
            <tbody>
              {models.length === 0 ? (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center', padding: 28 }} className="log-dash-muted">
                    {t(G.dash.emptyModels)}
                  </td>
                </tr>
              ) : (
                models.map((m) => {
                  const covered = rate(m.known_cost_requests, m.known_cost_requests + m.unknown_cost_requests)
                  return (
                    <tr key={m.key}>
                      <td>
                        <div className="log-dash-model" title={m.key}>
                          <i style={{ background: charts.colors.get(m.key) ?? OTHER_COLOR }} />
                          <span>{m.label}</span>
                        </div>
                      </td>
                      <td>
                        <span className="log-dash-share">
                          {fmtInt(m.requests)}
                          <span className="log-dash-share-bar">
                            <div style={{ width: `${(m.requests / maxReq) * 100}%` }} />
                          </span>
                        </span>
                      </td>
                      <td>{fmtPct(rate(m.success_requests, m.requests))}</td>
                      <td>{fmtInt(m.users)}</td>
                      <td>{fmtTokens(m.input_tokens)}</td>
                      <td>{fmtTokens(m.output_tokens)}</td>
                      <td>{fmtPct(rate(m.cached_tokens, m.input_tokens))}</td>
                      <td>{money.petals(m.user_cost)}</td>
                      <td title={t(G.dash.coverageShort, { p: fmtPct(covered) })}>{m.known_cost_requests > 0 ? money.usd(m.known_upstream_cost, 4) : '—'}</td>
                      <td>{fmtPct(margin(m))}</td>
                      <td>{fmtLatency(m.avg_duration_ms)}</td>
                      <td>{fmtLatency(m.p95_duration_ms)}</td>
                      <td>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onViewModel(m.key)}>
                          {t(G.dash.viewDetail)}
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ranking-grid">
        <div className="ranking-card">
          <div className="chart-card-head">
            <span>{t(G.dash.sourcesTitle)}</span>
            <small>{t(G.dash.sourcesHint)}</small>
          </div>
          {data.sources.length === 0 ? (
            <div className="log-dash-muted" style={{ padding: 16, textAlign: 'center', fontSize: 12 }}>
              {t(G.dash.noData)}
            </div>
          ) : (
            data.sources.map((r, i) => (
              <div key={r.source || '_'} className="rank-row log-dash-rank" onClick={() => r.source && onPickSource(r.source)}>
                <span className="rank-number">{i + 1}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="rank-name">{r.source || t(G.dash.mainSource)}</div>
                  <div className="rank-meta">{t(G.dash.rankMeta, { n: fmtInt(r.requests), t: fmtTokens(r.input_tokens + r.output_tokens), f: fmtInt(r.failed_requests) })}</div>
                </div>
                <div className="rank-value">{money.petals(r.user_cost)}</div>
              </div>
            ))
          )}
        </div>
        <div className="ranking-card">
          <div className="chart-card-head">
            <span>{t(G.dash.usersTitle)}</span>
            <small>{t(G.dash.usersHint)}</small>
          </div>
          {data.users.length === 0 ? (
            <div className="log-dash-muted" style={{ padding: 16, textAlign: 'center', fontSize: 12 }}>
              {t(G.dash.noData)}
            </div>
          ) : (
            data.users.map((r, i) => (
              <div key={r.user_id} className="rank-row log-dash-rank" onClick={() => onPickUser(r.user_id)}>
                <span className="rank-number">{i + 1}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="rank-name mono" title={r.user_id}>
                    {r.user_id}
                  </div>
                  <div className="rank-meta">{t(G.dash.rankMeta, { n: fmtInt(r.requests), t: fmtTokens(r.input_tokens + r.output_tokens), f: fmtInt(r.failed_requests) })}</div>
                </div>
                <div className="rank-value">{money.petals(r.user_cost)}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  )
}
